import { createSelector } from 'reselect'
import { RootState } from '../../types/rootState'
import { ClientTeamMembership } from '../../types/clientTeamMembership'

// Sort team members: REA -> LO -> Insurance -> Title Agent
const unknownTypeOrder = 99
const teamTypeOrder: Record<string, number> = {
  RealEstateAgent: 1,
  LoanOfficer: 2,
  InsuranceAgent: 3,
  TitleAgent: 4
}

const sortByTeamType = (memberships: ClientTeamMembership[]) =>
  [...memberships].sort((a, b) => {
    const orderA = teamTypeOrder[a.customerProfile.type] ?? unknownTypeOrder
    const orderB = teamTypeOrder[b.customerProfile.type] ?? unknownTypeOrder
    return orderA - orderB
  })

const selectClientTeamMembershipsState = (state: RootState) => state.clientTeamMemberships

export const selectClientTeamMemberships = createSelector(
  selectClientTeamMembershipsState,
  memberships => ({
    ...memberships,
    data: memberships?.data ? sortByTeamType(memberships.data) : []
  })
)

export const selectUnverifiedMembership = createSelector(
  selectClientTeamMemberships,
  memberships => memberships?.data?.find(membership => !membership.isVerified) || null
)

export const selectVerifiedClientTeamMemberships = createSelector(
  selectClientTeamMemberships,
  memberships => memberships?.data?.filter(membership => membership.isVerified) ?? []
)

export const selectClientHasUnverifiedAgent = createSelector(selectUnverifiedMembership, unverifiedMembership => {
  return unverifiedMembership !== null
})

export const selectPreferredLoanOfficerMembership = createSelector(
  selectClientTeamMemberships,
  memberships => memberships?.data?.find(membership => membership.customerProfile.type === 'LoanOfficer') || null
)

export const selectPreferredRealEstateAgentMembership = createSelector(
  selectClientTeamMemberships,
  memberships => memberships?.data?.find(membership => membership.customerProfile.type === 'RealEstateAgent') || null
)

export const selectPreferredInsuranceAgentMembership = createSelector(
  selectClientTeamMemberships,
  memberships => memberships?.data?.find(membership => membership.customerProfile.type === 'InsuranceAgent') || null
)

export const selectPreferredRealEstateAgentMembershipWithLoading = createSelector(
  selectClientTeamMemberships,
  memberships => ({
    data: memberships?.data?.find(membership => membership.customerProfile.type === 'RealEstateAgent') || null,
    loading: memberships?.loading ?? false
  })
)

export const selectPreferredInsuranceAgentMembershipWithLoading = createSelector(
  selectClientTeamMemberships,
  memberships => ({
    data: memberships?.data?.find(membership => membership.customerProfile.type === 'InsuranceAgent') || null,
    loading: memberships?.loading ?? false
  })
)
